import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getLiveToken } from '../../lib/auth';
import { Eye, EyeOff, Phone, Lock, Mail, Shield, X, CheckCircle2, AlertCircle, ArrowLeft } from 'lucide-react';
import baseurl from '../Baseurl/baseurl';
import { AuthLayout, FormField, Input, Button } from '../ui';

/**
 * The single sign-in page for every role.
 *
 * Member-first by design: there are ~282 members and 3 admins, so making
 * everyone pick a role up front would tax the majority to serve the few.
 * Members see their form immediately; admins switch with one tap.
 *
 * This matters most in the installed PWA, which has no address bar — before
 * this, /admin was unreachable from inside the app.
 *
 * `?role=admin` opens straight in admin mode, which is how /admin redirects here.
 */

// Each role differs only in endpoint, credential field, and where the token goes.
const ADMIN_ROLES = [
  { key: 'community', title: 'Community Admin', desc: 'Manage community content' },
  { key: 'super', title: 'Super Admin', desc: 'Full system access' },
];

const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [mode, setMode] = useState(searchParams.get('role') === 'admin' ? 'admin' : 'member');
  const [adminRole, setAdminRole] = useState('community');

  const [contactNumber, setContactNumber] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [approvalDialog, setApprovalDialog] = useState({ open: false, message: '' });

  const isAdmin = mode === 'admin';

  // Already signed in? Skip the form.
  //
  // getLiveToken, not a bare presence check: an expired token used to send the
  // user to /home, where ProtectedRoute bounced them straight back here, so the
  // form flashed in and out instead of just letting them sign in again.
  useEffect(() => {
    if (isAdmin) {
      if (getLiveToken('adminToken')) navigate('/admin/dashboard');
      return;
    }
    if (getLiveToken('token') && localStorage.getItem('memberData')) navigate('/home');
  }, [navigate, isAdmin]);

  const switchMode = (next) => {
    setMode(next);
    setError('');
    setPassword('');
    // Keep the URL honest so the mode survives a refresh or a shared link.
    setSearchParams(next === 'admin' ? { role: 'admin' } : {}, { replace: true });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleLogin();
  };

  const loginMember = async () => {
    const response = await fetch(`${baseurl}/api/member/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contact_no: contactNumber, password }),
    });
    const result = await response.json();

    if (!response.ok) {
      // Pending members get an explanation, not a bare "login failed".
      if (result.msg && result.msg.includes('not approved')) {
        setApprovalDialog({
          open: true,
          message: 'Your Profile is under review. For more information 730506436 (KBC Admin).',
        });
        return null;
      }
      throw new Error(result?.msg || 'Login failed');
    }

    // /api/member/login returns `accessToken`, not `token`.
    const token = result.accessToken || result.token;
    if (!token) throw new Error('Login succeeded but no token was returned');
    localStorage.setItem('token', token);
    localStorage.setItem('memberData', JSON.stringify(result.data));
    return { message: result.msg || 'Login successful!', to: '/home' };
  };

  const loginAdmin = async () => {
    const endpoint = adminRole === 'community'
      ? `${baseurl}/api/community_admin/login`
      : `${baseurl}/api/admin/login`;
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(
        adminRole === 'community' ? { email, password } : { email, password, role: adminRole }
      ),
    });
    const data = await response.json();

    if (!response.ok) throw new Error(data.msg || data.message || 'Login failed');

    // The two admin endpoints disagree on the field name: community_admin
    // returns `token`, admin returns `accessToken`.
    const token = adminRole === 'community' ? data.token : data.accessToken;
    if (!token) throw new Error('No token received from server');
    localStorage.setItem('adminToken', token);
    localStorage.setItem('adminRole', adminRole);
    return { message: data.msg || data.message || 'Login successful!', to: '/admin/dashboard' };
  };

  const handleLogin = async () => {
    setError('');
    const identifier = isAdmin ? email : contactNumber;
    if (!identifier || !password) {
      setError(
        isAdmin
          ? 'Email and password are required'
          : t('formErrors') || 'Contact number and password are required'
      );
      return;
    }

    try {
      setIsLoading(true);
      const result = isAdmin ? await loginAdmin() : await loginMember();
      if (!result) return; // approval dialog handled it
      setSnackbar({ open: true, message: result.message, severity: 'success' });
      navigate(result.to);
    } catch (err) {
      setError(err.message);
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleContactNumberChange = (e) => setContactNumber(e.target.value.replace(/\D/g, ''));

  const passwordToggle = (
    <button
      type="button"
      onClick={() => setShowPassword((s) => !s)}
      className="text-muted transition-colors hover:text-ink"
      aria-label={showPassword ? 'Hide password' : 'Show password'}
    >
      {showPassword ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
    </button>
  );

  return (
    <AuthLayout
      eyebrow={isAdmin ? 'KBC · Admin access' : 'KBC · Member access'}
      title={
        isAdmin
          ? <>Admin <em className="kbc-serif">login</em>.</>
          : <>Welcome <em className="kbc-serif">back</em>.</>
      }
      subtitle={
        isAdmin
          ? 'Select your role and sign in to your secure admin account.'
          : 'Sign in with your registered contact number to access the community directory.'
      }
      footer={
        isAdmin ? null : (
          <>
            {t('alreadyHaveAccount') || 'New to KBC?'}{' '}
            <Link to="/signup" className="font-semibold text-primary hover:text-primary-hi">
              {t('signup') || 'Create an account'}
            </Link>
          </>
        )
      }
    >
      <div className="space-y-5">
        {isAdmin && (
          <FormField label="Select Admin Role" required>
            <div className="grid grid-cols-2 gap-3">
              {ADMIN_ROLES.map((r) => {
                const active = adminRole === r.key;
                return (
                  <button
                    key={r.key}
                    type="button"
                    onClick={() => setAdminRole(r.key)}
                    aria-pressed={active}
                    className={`kbc-control rounded-md border p-3 text-center transition-colors duration-150 ${
                      active
                        ? 'border-primary/60 bg-primary/10'
                        : 'border-line/15 bg-surface hover:border-primary/30 hover:bg-primary/5'
                    }`}
                  >
                    <Shield className={`mx-auto mb-1.5 h-5 w-5 ${active ? 'text-primary' : 'text-muted'}`} />
                    <h3 className="mb-0.5 text-sm font-semibold text-ink">{r.title}</h3>
                    <p className="text-xs text-muted">{r.desc}</p>
                  </button>
                );
              })}
            </div>
          </FormField>
        )}

        {isAdmin ? (
          <FormField label="Email Address" htmlFor="email" required>
            <Input
              id="email"
              type="email"
              autoComplete="username"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyPress={handleKeyPress}
              leftIcon={<Mail className="h-[18px] w-[18px]" />}
            />
          </FormField>
        ) : (
          <FormField label="Contact Number" htmlFor="contact" required>
            <Input
              id="contact"
              type="tel"
              inputMode="numeric"
              autoComplete="username"
              placeholder="Enter your contact number"
              value={contactNumber}
              onChange={handleContactNumberChange}
              onKeyPress={handleKeyPress}
              leftIcon={<Phone className="h-[18px] w-[18px]" />}
            />
          </FormField>
        )}

        <FormField label={t('password') || 'Password'} htmlFor="password" required>
          <Input
            id="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={handleKeyPress}
            leftIcon={<Lock className="h-[18px] w-[18px]" />}
            rightIcon={passwordToggle}
          />
        </FormField>

        {!isAdmin && (
          <div className="flex justify-end">
            <Link to="/forgot-password" className="text-sm font-medium text-primary hover:text-primary-hi">
              Forgot Password?
            </Link>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 rounded-md border border-danger/20 bg-danger/10 px-4 py-3 text-sm font-medium text-danger">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {error}
          </div>
        )}

        <Button onClick={handleLogin} loading={isLoading} fullWidth size="lg">
          {isLoading
            ? 'Signing in…'
            : isAdmin
              ? `Sign in as ${adminRole === 'community' ? 'Community Admin' : 'Super Admin'}`
              : t('signin') || 'Sign In'}
        </Button>

        {/* The only way into the admin console from inside the installed app. */}
        <div className="border-t border-line/10 pt-4 text-center">
          {isAdmin ? (
            <button
              type="button"
              onClick={() => switchMode('member')}
              className="kbc-control inline-flex items-center gap-1.5 text-sm font-medium text-muted transition-colors hover:text-ink"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to member sign-in
            </button>
          ) : (
            <button
              type="button"
              onClick={() => switchMode('admin')}
              className="kbc-control inline-flex items-center gap-1.5 text-sm font-medium text-muted transition-colors hover:text-ink"
            >
              <Shield className="h-3.5 w-3.5" />
              Administrator sign-in
            </button>
          )}
        </div>
      </div>

      {/* Snackbar */}
      {snackbar.open && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 animate-slideUp">
          <div
            className={`flex items-center gap-3 rounded-full px-5 py-3 text-sm font-medium text-white shadow-lift ${
              snackbar.severity === 'success' ? 'bg-success' : 'bg-danger'
            }`}
          >
            {snackbar.severity === 'success' ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
            <span>{snackbar.message}</span>
            <button onClick={() => setSnackbar({ ...snackbar, open: false })} className="opacity-80 hover:opacity-100">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Approval dialog */}
      {approvalDialog.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/60 px-4 animate-fadeIn">
          <div className="w-full max-w-md rounded-lg border border-line/12 bg-surface p-6 shadow-lift animate-slideUp">
            <h3 className="font-display text-xl font-semibold tracking-tight text-ink">Account approval required</h3>
            <p className="mt-2 text-sm leading-relaxed text-text">{approvalDialog.message}</p>
            <div className="mt-5 flex justify-end">
              <Button onClick={() => setApprovalDialog({ open: false, message: '' })}>OK</Button>
            </div>
          </div>
        </div>
      )}
    </AuthLayout>
  );
};

export default LoginPage;
